<template>
  <section class="white-model-workspace">
    <div class="white-model-main">
      <aside class="white-model-settings">
        <section class="setting-section">
          <header class="section-title">
            <span>01</span>
            <div><strong>上传家具原图</strong><small>{{ sceneMode ? '建议使用单件家具白底图或背景干净的商品图' : '建议主体完整、边缘清晰，支持 PNG / JPG / WebP' }}</small></div>
          </header>

          <label class="plan-dropzone" :class="{ filled: previewUrl }">
            <input type="file" accept="image/png,image/jpeg,image/webp" @change="handleFile" />
            <img v-if="previewUrl" :src="$ossImageUrl(previewUrl, 'preview')" alt="待处理的家具原图" decoding="async" />
            <span v-else class="dropzone-empty">
              <i class="el-icon-upload2"></i>
              <strong>选择单件家具图片</strong>
              <small>沙发、座椅、桌柜、灯具等均可处理</small>
            </span>
            <button v-if="previewUrl" type="button" class="replace-plan">更换</button>
          </label>

          <div class="file-rule" :class="{ valid: previewUrl }">
            <i :class="previewUrl ? 'el-icon-circle-check' : 'el-icon-warning-outline'"></i>
            <span>{{ previewUrl ? floorPlanFile.name : '请上传一张主体完整的家具图片，不要上传拼图' }}</span>
          </div>
        </section>

        <section v-if="sceneMode" class="setting-section dimension-section">
          <header class="section-title">
            <span>02</span>
            <div><strong>设置目标环境</strong><small>选择空间、风格和光线，AI 将自动完成真实融合</small></div>
          </header>
          <label class="scene-field">
            <span>空间类型</span>
            <select v-model="sceneSpace" aria-label="选择空间类型">
              <option v-for="item in sceneSpaces" :key="item" :value="item">{{ item }}</option>
            </select>
          </label>
          <label class="scene-field">
            <span>设计风格</span>
            <select v-model="sceneStyle" aria-label="选择设计风格">
              <option v-for="item in sceneStyles" :key="item" :value="item">{{ item }}</option>
            </select>
          </label>
          <div class="output-row lighting-outline-row">
            <span>光线氛围</span>
            <el-radio-group v-model="lightingMode" size="small" aria-label="选择光线氛围">
              <el-radio-button label="自然日光">自然日光</el-radio-button>
              <el-radio-button label="暖光氛围">暖光氛围</el-radio-button>
            </el-radio-group>
          </div>
          <label class="note-field">
            <span>场景要求 <em>{{ lineNote.length }}/300</em></span>
            <textarea v-model="lineNote" maxlength="300" placeholder="例如：放在落地窗旁，搭配浅色地毯和绿植"></textarea>
            <AiPromptOptimizer
              v-model="lineNote"
              :disabled="generating"
              :max-length="300"
              context="家具白底图生成室内搭配场景的要求"
            />
          </label>
        </section>

        <section v-else class="setting-section dimension-section">
          <header class="section-title">
            <span>02</span>
            <div><strong>设置白底表现</strong><small>默认保留自然接地阴影，商品主体边缘干净清晰</small></div>
          </header>
          <div class="output-row lighting-outline-row">
            <span>边缘处理</span>
            <el-radio-group v-model="lightingMode" size="small" aria-label="选择家具边缘处理">
              <el-radio-button label="精细">精细</el-radio-button>
              <el-radio-button label="标准">标准</el-radio-button>
            </el-radio-group>
          </div>
          <label class="note-field">
            <span>补充要求 <em>{{ lineNote.length }}/300</em></span>
            <textarea
              v-model="lineNote"
              maxlength="300"
              placeholder="例如：保留藤编孔隙、玻璃透明边缘和金属反光"
            ></textarea>
            <AiPromptOptimizer
              v-model="lineNote"
              :disabled="generating"
              :max-length="300"
              context="家具或软装抠图与透明背景处理的补充要求"
            />
          </label>
        </section>

        <section class="setting-section output-section">
          <header class="section-title">
            <span>03</span>
            <div><strong>{{ sceneMode ? '环境图输出' : '商品图输出' }}</strong><small>默认跟随原图画幅，也可以手动调整</small></div>
          </header>
          <div class="output-row">
            <span>画幅</span>
            <AspectRatioSelector
              v-model="selectedRatio"
              compact
              :disabled="generating"
              :options="ratioOptions"
              :aria-label="sceneMode ? '选择家具环境图画幅' : '选择家具商品图画幅'"
            />
          </div>
          <div class="output-row">
            <span>分辨率</span>
            <ResolutionSelector v-model="selectedResolution" compact :disabled="generating" :show-label="false" />
          </div>
          <div class="output-row">
            <span>生成质量</span>
            <QualitySelector v-model="selectedQuality" compact :disabled="generating" />
          </div>
        </section>

        <button class="generate-white-model" :disabled="generating" @click="generateWhiteModel">
          <span v-if="generating" class="button-spinner"></span>
          <i v-else class="el-icon-edit-outline"></i>
          <span>{{ generating ? generationText : (sceneMode ? '生成家具环境图' : '生成家具白底图') }}</span>
          <small v-if="!generating">{{ sceneMode ? '保留家具原貌并生成 1 张真实场景图' : '自动去背景并生成 1 张商品图' }}</small>
        </button>
        <div v-if="generating" class="generation-progress"><i :style="{ width: `${progress}%` }"></i></div>
      </aside>

      <section class="comparison-panel">
        <header class="comparison-head">
          <div>
            <strong>家具原图与{{ sceneMode ? '环境效果图' : '白底商品图' }}对照</strong>
            <span>{{ sceneMode ? '生成后重点核对家具还原度、空间尺度、透视、落地阴影与光线融合' : '生成后重点核对造型、比例、颜色、材质纹理、镂空与透明边缘' }}</span>
          </div>
          <div class="comparison-actions">
            <WorkflowTuningButton
              v-if="resultUrl"
              variant="button"
              icon-only
              :image-url="resultUrl"
              :prompt="finalPrompt"
              source-model="gpt-image-2-official"
              :module-name="sceneMode ? '家具环境生成' : '家具白底渲染'"
            />
            <button
              v-if="resultUrl"
              title="下载家具白底图"
              aria-label="下载家具白底图"
              @click="downloadResult"
            ><i class="el-icon-download"></i></button>
            <button
              v-if="resultUrl"
              title="重新生成"
              aria-label="重新生成"
              :disabled="generating"
              @click="generateWhiteModel"
            ><i class="el-icon-refresh"></i></button>
          </div>
        </header>

        <div class="comparison-stage" :class="{ 'has-result': resultUrl }">
          <article class="compare-viewport source-viewport">
            <span class="viewport-label">A · 家具原图</span>
            <img
              v-if="previewUrl"
              class="zoomable-stage-image"
              :src="$ossImageUrl(previewUrl, 'preview')"
              alt="家具原图"
              title="点击放大家具原图"
              role="button"
              tabindex="0"
              decoding="async"
              @click="previewImage(previewUrl, '家具白底渲染 · 家具原图', currentViewerItems, 0)"
              @keydown.enter.prevent="previewImage(previewUrl, '家具白底渲染 · 家具原图', currentViewerItems, 0)"
              @keydown.space.prevent="previewImage(previewUrl, '家具白底渲染 · 家具原图', currentViewerItems, 0)"
            />
            <div v-else class="viewport-empty">
              <i class="el-icon-picture-outline"></i>
              <strong>等待家具图片</strong>
              <span>上传后在这里检查主体是否完整、清晰</span>
            </div>
          </article>
          <article class="compare-viewport model-viewport">
            <span class="viewport-label">B · {{ sceneMode ? '家具环境效果图' : '纯白背景商品图' }}</span>
            <img
              v-if="resultUrl"
              class="zoomable-stage-image"
              :src="$ossImageUrl(resultUrl, 'preview')"
              :alt="sceneMode ? 'AI生成的家具环境效果图' : 'AI生成的家具纯白背景商品图'"
              title="点击放大家具白底图"
              role="button"
              tabindex="0"
              decoding="async"
              @click="previewImage(resultUrl, '家具白底渲染 · 纯白背景商品图', currentViewerItems, previewUrl ? 1 : 0)"
              @keydown.enter.prevent="previewImage(resultUrl, '家具白底渲染 · 纯白背景商品图', currentViewerItems, previewUrl ? 1 : 0)"
              @keydown.space.prevent="previewImage(resultUrl, '家具白底渲染 · 纯白背景商品图', currentViewerItems, previewUrl ? 1 : 0)"
            />
            <div v-else-if="generating" class="viewport-empty model-loading">
              <span class="model-loader"><i></i><i></i><i></i></span>
              <strong>{{ generationText }}</strong>
              <span>{{ sceneMode ? '正在分析家具特征、构建空间并匹配真实光影' : '正在识别家具主体、清理背景并精修边缘' }}</span>
            </div>
            <div v-else class="viewport-empty white-model-empty">
              <i class="el-icon-picture-outline line-art-empty-icon"></i>
              <strong>等待生成{{ sceneMode ? '环境图' : '白底图' }}</strong>
              <span>{{ sceneMode ? '家具主体准确还原，空间、透视和光影自然融合' : '纯白背景、主体居中、边缘干净并保留自然接地阴影' }}</span>
            </div>
          </article>
        </div>

        <div class="accuracy-strip">
          <span><i class="el-icon-check"></i> 造型比例不变</span>
          <span><i class="el-icon-check"></i> 颜色材质不变</span>
          <span><i class="el-icon-check"></i> {{ sceneMode ? '空间尺度合理' : '纯白无缝背景' }}</span>
          <span><i class="el-icon-check"></i> {{ sceneMode ? '透视光影匹配' : '精修复杂边缘' }}</span>
          <span><i class="el-icon-check"></i> 保留自然接地阴影</span>
        </div>

        <div v-if="errorMessage" class="generation-error">
          <i class="el-icon-warning-outline"></i>
          <div>
            <strong>{{ errorMessage }}</strong>
            <span>{{ errorSuggestion }}</span>
          </div>
          <button v-if="currentTaskIds.length && errorRetryable" :disabled="generating" @click="continuePolling">
            继续查询
          </button>
        </div>
      </section>
    </div>

    <section class="ai-method-panel">
      <header class="method-head">
        <div>
          <span class="workspace-kicker">AI METHOD & PROMPT</span>
          <h2>AI 是怎样生成家具{{ sceneMode ? '环境效果图' : '白底商品图' }}的</h2>
        </div>
        <button class="prompt-toggle" @click="promptVisible = !promptVisible">
          <i :class="promptVisible ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          {{ promptVisible ? '收起提示词' : '查看对应提示词' }}
        </button>
      </header>

      <div class="method-steps">
        <article v-for="item in methodSteps" :key="item.number">
          <span>{{ item.number }}</span>
          <i :class="item.icon"></i>
          <div><strong>{{ item.title }}</strong><p>{{ item.description }}</p></div>
        </article>
      </div>

      <transition name="prompt-reveal">
        <div v-if="promptVisible" class="prompt-content">
          <section class="prompt-block system-prompt-block">
            <header>
              <div><span>SYSTEM PROMPT</span><strong>家具{{ sceneMode ? '环境生成' : '白底渲染' }}系统提示词</strong></div>
              <button title="复制系统提示词" aria-label="复制系统提示词" @click="copyText(systemPrompt)">
                <i class="el-icon-copy-document"></i>
              </button>
            </header>
            <pre>{{ systemPrompt }}</pre>
          </section>

          <section class="prompt-block">
            <header>
              <div><span>FINAL PROMPT</span><strong>本次提交给图片模型的提示词</strong></div>
              <button title="复制最终提示词" aria-label="复制最终提示词" @click="copyText(finalPrompt)">
                <i class="el-icon-copy-document"></i>
              </button>
            </header>
            <pre>{{ finalPrompt }}</pre>
          </section>

          <section class="negative-constraints">
            <strong>负向约束</strong>
            <span v-for="item in negativeConstraints" :key="item">{{ item }}</span>
          </section>
        </div>
      </transition>
    </section>

    <section class="furniture-history-section">
      <header class="history-head">
        <div>
          <span class="workspace-kicker">GENERATION RECORDS</span>
          <h2>家具{{ sceneMode ? '环境' : '白底' }}生成记录</h2>
          <p>仅展示当前功能的生成任务，点击记录可恢复查看；点击原图或结果图可放大。</p>
        </div>
        <button type="button" class="history-refresh" :disabled="historyLoading" @click="loadPendingTask">
          <i :class="historyLoading ? 'el-icon-loading' : 'el-icon-refresh'"></i>
          {{ historyLoading ? '正在刷新' : '刷新记录' }}
        </button>
      </header>

      <div v-if="history.length" class="furniture-history-grid">
        <article
          v-for="record in history"
          :key="record.id"
          :class="{ active: currentBatchId === record.id }"
          role="button"
          tabindex="0"
          :aria-label="`恢复${sceneMode ? '家具环境' : '家具白底'}生成记录`"
          @click="restoreHistoryRecord(record)"
          @keydown.enter.prevent="restoreHistoryRecord(record)"
          @keydown.space.prevent="restoreHistoryRecord(record)"
        >
          <div class="history-preview">
            <span class="history-status" :class="record.status">{{ statusText(record.status) }}</span>
            <button
              type="button"
              class="history-image source"
              :disabled="!record.source"
              aria-label="放大查看历史家具原图"
              @click.stop="previewHistoryImage(record, 'source')"
            >
              <img v-if="record.source" :src="$ossImageUrl(record.source, 'thumbnail')" alt="历史家具原图" loading="lazy" decoding="async" />
              <span v-else><i class="el-icon-picture-outline"></i>原图不可用</span>
              <small>原图</small>
              <i v-if="record.source" class="el-icon-zoom-in history-zoom" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="history-image result"
              :disabled="!record.result"
              :aria-label="record.result ? `放大查看历史家具${sceneMode ? '环境' : '白底'}结果图` : statusText(record.status)"
              @click.stop="previewHistoryImage(record, 'result')"
            >
              <img v-if="record.result" :src="$ossImageUrl(record.result, 'thumbnail')" alt="历史家具白底结果图" loading="lazy" decoding="async" />
              <span v-else><i :class="record.status === 'generating' ? 'el-icon-loading' : 'el-icon-warning-outline'"></i>{{ statusText(record.status) }}</span>
              <small>{{ sceneMode ? '环境图' : '白底图' }}</small>
              <i v-if="record.result" class="el-icon-zoom-in history-zoom" aria-hidden="true"></i>
            </button>
          </div>
          <ImageDimensionsRow :items="[{ label: '原图尺寸', url: record.source, probe: true }, { label: '结果尺寸', url: record.result, width: record.resultWidth, height: record.resultHeight }]" />
          <GenerationModelDetails :record="record" />
          <GenerationFailureDetails :record="record" />
          <footer>
            <div>
              <strong>{{ record.style || (sceneMode ? '家具环境生成' : '纯白背景商品渲染') }}</strong>
              <dl class="history-record-details">
                <div>
                  <dt>图片比例</dt>
                  <dd>{{ record.size || '1:1' }}</dd>
                </div>
                <div>
                  <dt>生成时间</dt>
                  <dd><time class="creation-date" :datetime="record.createdAt || undefined">{{ formatRecordTime(record.createdAt) }}</time></dd>
                </div>
              </dl>
            </div>
            <WorkflowTuningButton
              v-if="record.result"
              variant="button"
              icon-only
              :image-url="record.result"
              :prompt="record.prompt || finalPrompt"
              :source-model="record.model || ''"
              :module-name="sceneMode ? '家具环境生成' : '家具白底渲染'"
            />
            <button
              v-if="record.result"
              type="button"
              title="下载这张家具白底图"
              aria-label="下载这张家具白底图"
              @click.stop="downloadHistoryResult(record)"
            ><i class="el-icon-download"></i></button>
          </footer>
        </article>
      </div>

      <div v-else class="history-empty">
        <i class="el-icon-time"></i>
        <strong>{{ historyLoading ? '正在加载生成记录' : `还没有家具${sceneMode ? '环境' : '白底'}生成记录` }}</strong>
        <span>完成第一次生成后，原图、结果和任务状态会自动保存在这里。</span>
      </div>
      <AppPagination :page="historyPage" :page-size="12" :total="historyTotal" :loading="historyLoading" @change="changeHistoryPage" />
    </section>
  </section>
</template>

<script>
/* 家具抠图先生成透明背景单品，再进入摆放/渲染流程；透明图和展示图的 URL 不要混用。 */
import { apiRequest, downloadResource } from '@/services/api'
import AspectRatioSelector from '@/components/AspectRatioSelector.vue'
import ResolutionSelector from '@/components/ResolutionSelector.vue'
import QualitySelector from '@/components/QualitySelector.vue'
import ImageDimensionsRow from '@/components/ImageDimensionsRow.vue'
import AiPromptOptimizer from '@/components/AiPromptOptimizer.vue'
import { mergePendingGenerations, removePendingGeneration, savePendingGeneration } from '@/utils/pendingGenerationRecords'
import { DEFAULT_GENERATION_QUALITY, MINIMUM_OUTPUT_RESOLUTION } from '@/utils/outputQuality'
import { closestImageRatio, SUPPORTED_IMAGE_RATIOS } from '@/utils/imageAspectRatio'

const FALLBACK_SYSTEM_PROMPT = `你是专业家具商品摄影后期与高精度抠图专家。你的唯一任务，是把用户上传的单张家具图片转换为纯白背景的标准商品展示图。

强制规则：
1. 输入图片是家具主体的唯一依据。完整保留家具的品类、造型、比例、结构、视角、颜色、材质、纹理、五金、缝线、褶皱和磨损特征，不得重新设计或美化变形。
2. 只移除家具以外的原背景、地面、墙体、环境物、杂物、文字和原场景投影；不得误删家具部件、支脚、扶手、靠垫、纤细结构或装饰细节。
3. 输出背景必须是均匀纯白色 RGB(255,255,255)，无渐变、无灰角、无墙地分界、无棚拍背景线、无透明棋盘格。
4. 主体完整居中，四周留白均衡，不裁切家具边缘，不改变原视角。主体占画面约 75% 至 85%，但不能通过拉伸或压缩改变比例。
5. 精细处理毛绒、藤编、网布、玻璃、亚克力、镂空、金属反光等复杂边缘。透明或半透明部件保持真实透明关系，不产生白边、黑边、锯齿、色溢或残留背景。
6. 可在家具接地点下方保留非常轻柔、真实、方向一致的中性灰接触阴影，使主体不悬浮；不得保留原环境的大面积投影，不得添加倒影或戏剧性布光。
7. 禁止新增配件、改色、换材质、修改变形、补造不可见结构、添加人物、植物、道具、文字、Logo、水印、边框或拼图。只输出一张最终白底商品图。`

const SCENE_SYSTEM_PROMPT = `你是专业家具商业摄影与室内场景设计师。以用户上传的单件家具为唯一主体依据，完整保留其造型、比例、结构、颜色、材质、纹理和可见细节。根据指定空间、风格与光线生成真实、完整、可落地的室内环境，使家具尺度、透视、落地点、接触阴影、反射与环境光自然一致。不得改款、改色、换材质、增删部件或遮挡主体；不得输出白底图、拼图、文字、Logo 或水印。`

export default {
  name: 'FurnitureCutoutRender',
  components: { AspectRatioSelector, ResolutionSelector, QualitySelector, ImageDimensionsRow, AiPromptOptimizer },
  props: {
    sceneMode: { type: Boolean, default: false }
  },
  data() {
    return {
      floorPlanFile: null,
      previewUrl: '',
      lightingMode: this.sceneMode ? '自然日光' : '精细',
      lineNote: '',
      selectedRatio: this.sceneMode ? '16:9' : '1:1',
      ratioOptions: SUPPORTED_IMAGE_RATIOS,
      sceneSpace: '客厅',
      sceneStyle: '现代简约',
      sceneSpaces: ['客厅', '餐厅', '卧室', '书房', '玄关', '商业展厅'],
      sceneStyles: ['现代简约', '意式轻奢', '原木奶油', '中古风', '新中式', '侘寂风'],
      selectedResolution: MINIMUM_OUTPUT_RESOLUTION,
      selectedQuality: DEFAULT_GENERATION_QUALITY,
      apiConfigured: false,
      generating: false,
      progress: 0,
      generationText: this.sceneMode ? '准备生成家具环境' : '准备上传家具图片',
      resultUrl: '',
      errorMessage: '',
      errorSuggestion: '',
      errorRetryable: false,
      currentBatchId: '',
      currentTaskIds: [],
      promptVisible: true,
      systemPrompt: this.sceneMode ? SCENE_SYSTEM_PROMPT : FALLBACK_SYSTEM_PROMPT,
      requestControllers: [],
      pollingStopped: false,
      pendingRecordTimer: null,
      history: [],
      historyLoading: false,
      historyPage: 1,
      historyTotal: 0,
      methodSteps: this.sceneMode ? [
        { number: '01', icon: 'el-icon-picture-outline', title: '识别家具主体', description: '锁定家具造型、比例、材质、颜色、视角及完整细节。' },
        { number: '02', icon: 'el-icon-house', title: '构建目标空间', description: '按照空间用途与设计风格生成协调、可落地的室内环境。' },
        { number: '03', icon: 'el-icon-sunny', title: '匹配透视光影', description: '统一尺度、镜头、落地点、接触阴影、反射和环境色温。' },
        { number: '04', icon: 'el-icon-view', title: '校验主体还原', description: '检查家具不变形、不改色，主体突出且与场景自然融合。' }
      ] : [
        { number: '01', icon: 'el-icon-picture-outline', title: '识别家具主体', description: '锁定家具的完整外形、部件、视角、颜色、材质与细节。' },
        { number: '02', icon: 'el-icon-scissors', title: '清除原始背景', description: '移除墙地、环境杂物和原场景投影，避免误删细小部件。' },
        { number: '03', icon: 'el-icon-magic-stick', title: '精修边缘与阴影', description: '处理毛绒、藤编、玻璃、镂空等复杂边缘，并生成克制接地阴影。' },
        { number: '04', icon: 'el-icon-view', title: '校验商品图', description: '检查主体无变形、纯白背景均匀、边缘无色溢且四周留白合理。' }
      ],
      negativeConstraints: this.sceneMode ? [
        '不改家具造型', '不改变颜色材质', '不增删部件', '不遮挡主体',
        '不复制原图背景', '透视尺度合理', '光影方向一致', '不输出白底图', '不输出文字水印'
      ] : [
        '不改家具造型', '不改变颜色材质', '不裁切主体', '不误删细小部件',
        '无原背景残留', '无白边黑边', '无灰色背景', '不添加道具', '不输出文字水印'
      ]
    }
  },
  computed: {
    currentViewerItems() {
      return [
        this.previewUrl ? { src: this.previewUrl, title: '家具白底渲染 · 家具原图' } : null,
        this.resultUrl ? { src: this.resultUrl, title: '家具白底渲染 · 纯白背景商品图' } : null
      ].filter(Boolean)
    },
    stylePrompt() {
      if (this.sceneMode) return `目标空间：${this.sceneSpace}；设计风格：${this.sceneStyle}；光线氛围：${this.lightingMode}`
      const edge = this.lightingMode === '精细'
        ? '高精度处理毛绒、藤编、网布、玻璃、镂空与金属反光边缘'
        : '使用自然干净的标准商品抠图边缘'
      return `接地阴影：保留克制、真实的中性灰自然接触阴影；边缘处理：${edge}`
    },
    finalPrompt() {
      const note = this.lineNote.trim() || '无额外要求，严格保持家具原貌'
      if (this.sceneMode) return `【单件家具环境场景生成】
输入图片中的家具是唯一主体依据，完整保留其品类、造型、比例、结构、视角、颜色、材质、纹理、五金与所有可见细节，不得改款、改色、换材质、增删部件或变形。
${this.stylePrompt}。围绕该家具生成完整、写实、可落地的室内环境，让家具成为清晰的视觉主体。匹配真实尺度、镜头透视、落地点、遮挡关系、接触阴影、反射、景深与环境色温；可添加符合空间用途的墙地顶、窗户、灯具、地毯、绿植及少量陪衬家具，但不得遮挡主体或喧宾夺主。
补充要求：${note}。
禁止复制原图背景，禁止输出白底商品图、对比拼图、文字、Logo、水印或边框。只输出一张最终环境效果图，不输出解释。`
      return `【家具图片转纯白背景商品图】
家具原图是唯一主体依据。完整保留家具品类、造型、比例、结构、视角、颜色、材质、纹理、五金、缝线、褶皱及所有可见部件，只移除家具以外的原背景、地面、墙体、环境物、杂物和原场景投影。
输出要求：均匀纯白背景 RGB(255,255,255)，无渐变、无灰角、无墙地分界、无透明棋盘格。主体完整居中，四周留白均衡，占画面约 75% 至 85%，不得裁边、拉伸或压缩。${this.stylePrompt}。透明或半透明部件保持真实透明关系；避免白边、黑边、锯齿、色溢和背景残留。
补充要求：${note}。
禁止重新设计家具，禁止增删部件、改色、换材质、修改变形、补造不可见结构；禁止添加人物、植物、道具、文字、Logo、水印、边框或拼图。只输出一张最终白底商品图，不输出解释。`
    }
  },
  created() {
    this.loadConfiguration()
    this.loadPendingTask()
  },
  beforeDestroy() {
    this.pollingStopped = true
    if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer)
    this.requestControllers.forEach(controller => controller.abort())
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
  },
  methods: {
    previewImage(image, title, items = [], index = null) {
      if (!image) return
      this.$emit('preview-image', image, { title, items, index })
    },
    async request(url, options = {}) {
      return apiRequest(url, options, {
        controllers: this.requestControllers,
        isStopped: () => this.pollingStopped
      })
    },
    async loadConfiguration() {
      try {
        const health = await this.request('/api/health')
        this.apiConfigured = Boolean(health.configured)
      } catch (error) {
        this.apiConfigured = false
      }

      if (!this.sceneMode) {
        try {
          const prompt = await this.request('/api/prompts/furniture-cutout')
          if (prompt.systemPrompt) this.systemPrompt = prompt.systemPrompt
        } catch (error) {
          this.systemPrompt = FALLBACK_SYSTEM_PROMPT
        }
      }
    },
    handleFile(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
        this.showMessage('仅支持 PNG、JPG 或 WebP 家具图片', 'warning')
        return
      }
      if (file.size >= 10 * 1024 * 1024) {
        this.showMessage('家具图片必须小于 10MB，请压缩后再上传', 'warning')
        return
      }
      const previewUrl = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
        this.floorPlanFile = file
        this.previewUrl = previewUrl
        this.selectedRatio = closestImageRatio(image.naturalWidth, image.naturalHeight, this.ratioOptions)
        this.resultUrl = ''
        this.errorMessage = ''
        this.errorSuggestion = ''
        this.errorRetryable = false
        this.currentBatchId = ''
        this.currentTaskIds = []
      }
      image.onerror = () => {
        URL.revokeObjectURL(previewUrl)
        this.showMessage('家具图片已损坏或无法读取，请重新选择', 'error')
      }
      image.src = previewUrl
    },
    validateLineSettings() {
      if (this.lineNote.length > 300) {
        throw new Error('补充要求不能超过 300 个字符')
      }
    },
    async uploadPlan() {
      const form = new FormData()
      form.append('file', this.floorPlanFile)
      return this.request('/api/images/upload', { method: 'POST', body: form, timeoutMs: 120000 })
    },
    taskImage(task) {
      const data = task && task.result && task.result.data
      return Array.isArray(data) && data[0] ? data[0].url : ''
    },
    async pollTask(taskId) {
      const startedAt = Date.now()
      let queryFailures = 0
      while (Date.now() - startedAt < 600000 && !this.pollingStopped) {
        try {
          const task = await this.request(`/api/generations/${encodeURIComponent(taskId)}`, {
            timeoutMs: 12000,
            retries: 1,
            timeoutMessage: '家具白底任务状态查询超时'
          })
          queryFailures = 0
          this.progress = Math.max(this.progress, Math.min(96, Number(task.progress || 0)))
          if (task.status === 'completed') {
            const image = this.taskImage(task)
            if (!image) {
              const error = new Error('任务已完成，但未返回可用的家具白底图')
              error.suggestion = '请联系管理员并提供任务编号，原任务记录已保留。'
              error.retryable = false
              throw error
            }
            return image
          }
          if (task.status === 'failed') {
            const error = new Error(task.error && task.error.message ? task.error.message : 'AI 家具白底渲染失败')
            Object.assign(error, task.error || {}, { terminal: true })
            throw error
          }
          this.generationText = this.sceneMode
            ? (this.progress > 45 ? `正在融合空间光影 ${this.progress}%` : `正在构建目标环境 ${this.progress}%`)
            : (this.progress > 45 ? `正在校验商品图 ${this.progress}%` : `正在清理背景 ${this.progress}%`)
        } catch (error) {
          if (error.terminal || error.retryable === false) throw error
          queryFailures += 1
          this.generationText = `网络波动，正在恢复白底渲染进度（${queryFailures}/5）`
          if (queryFailures >= 5) {
            const pending = new Error('暂时无法查询白底渲染进度，但任务仍可能在后台处理')
            pending.code = 'POLLING_UNAVAILABLE'
            pending.suggestion = '请恢复网络后点击“继续查询”，无需重新上传或创建任务。'
            pending.retryable = true
            pending.pending = true
            throw pending
          }
        }
        await new Promise(resolve => window.setTimeout(resolve, 3000))
      }
      const pending = new Error('家具白底渲染时间较长，任务仍在后台处理中')
      pending.code = 'TASK_STILL_RUNNING'
      pending.suggestion = '请稍后点击“继续查询”，无需重复创建任务。'
      pending.retryable = true
      pending.pending = true
      throw pending
    },
    async generateWhiteModel() {
      if (this.generating) return
      if (!this.floorPlanFile) {
        this.showMessage('请先上传一张主体完整的家具图片', 'warning')
        return
      }
      if (!this.apiConfigured) {
        this.showMessage('请先配置 ToAPIs API Key', 'warning')
        return
      }
      try {
        this.validateLineSettings()
      } catch (error) {
        this.showMessage(error.message, 'warning')
        return
      }

      this.generating = true
      this.errorMessage = ''
      this.errorSuggestion = ''
      this.errorRetryable = false
      this.resultUrl = ''
      this.progress = 4
      this.generationText = '正在上传家具图片'
      const random = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`
      const requestId = `${this.sceneMode ? 'furniture-scene' : 'furniture-cutout'}-${random}`.slice(0, 64)
      const pendingId = `interior-${requestId}`
      const createdAt = new Date().toISOString()
      this.savePendingTask(pendingId, createdAt)
      let generationRequestStarted = false
      try {
        const asset = await this.uploadPlan()
        this.savePendingTask(pendingId, createdAt, asset.localUrl)
        this.progress = 12
        this.generationText = this.sceneMode ? '正在创建家具环境生成任务' : '正在创建家具白底渲染任务'
        generationRequestStarted = true
        const batch = await this.request('/api/generations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrls: [asset.url],
            prompt: this.finalPrompt,
            space: this.sceneMode ? this.sceneSpace : '单件家具商品图',
            style: this.sceneMode ? `${this.sceneStyle} · ${this.lightingMode}` : '纯白背景商品渲染',
            size: this.selectedRatio,
            resolution: this.selectedResolution,
            quality: this.selectedQuality,
            count: 1,
            stage: this.sceneMode ? 'furniture_scene' : 'furniture_cutout',
            floorPlanRecordUrls: asset.localUrl ? [asset.localUrl] : [],
            referenceRecordUrls: [],
            requestId
          }),
          timeoutMs: 120000,
          retries: 1,
          timeoutMessage: '创建家具白底任务等待超时，正在确认是否已成功提交'
        })
        const task = (batch.tasks || []).find(item => item && item.id)
        if (!task) {
          const pending = new Error('任务已保存，正在后台确认家具白底任务编号')
          pending.suggestion = '请稍后继续查询，无需重复创建任务。'
          pending.retryable = true
          pending.pending = true
          throw pending
        }
        this.currentBatchId = batch.batchId || pendingId
        this.currentTaskIds = [task.id]
        removePendingGeneration(pendingId)
        this.progress = 18
        this.resultUrl = await this.pollTask(task.id)
        this.progress = 100
        this.generationText = this.sceneMode ? '家具环境图已生成' : '家具白底图已生成'
        this.errorSuggestion = ''
        this.showMessage(this.sceneMode ? '家具环境图已生成，请核对主体还原与场景融合' : '家具白底图已生成，请与左侧原图逐项核对', 'success')
      } catch (error) {
        this.errorMessage = error.message || '家具白底渲染失败，请稍后重试'
        this.errorSuggestion = error.suggestion || '请检查家具图片和网络后重新尝试；若任务已创建，请优先继续查询。'
        this.errorRetryable = Boolean(error.pending || error.retryable)
        if (generationRequestStarted && (error.retryable || error.pending)) this.schedulePendingTaskRefresh()
        else if (!this.currentTaskIds.length) removePendingGeneration(pendingId)
        this.showMessage(this.errorMessage, error.pending ? 'warning' : 'error')
      } finally {
        this.generating = false
        this.loadPendingTask()
      }
    },
    savePendingTask(id, createdAt, localUrl = '') {
      savePendingGeneration({
        id,
        space: this.sceneMode ? this.sceneSpace : '单件家具商品图',
        style: this.sceneMode ? `${this.sceneStyle} · ${this.lightingMode}` : '纯白背景商品渲染',
        size: this.selectedRatio,
        prompt: this.finalPrompt,
        status: 'generating',
        stage: this.sceneMode ? 'furniture_scene' : 'furniture_cutout',
        progress: 0,
        expectedCount: 1,
        tasks: [],
        images: [],
        inputContext: {
          floorPlanImages: localUrl ? [{ type: 'original', label: '家具原图', url: localUrl }] : [],
          referenceImages: [],
          annotation: null
        },
        createdAt
      })
    },
    async loadPendingTask() {
      if (this.historyLoading) return
      this.historyLoading = true
      try {
        const stage = this.sceneMode ? 'furniture_scene' : 'furniture_cutout'
        const response = await this.request(`/api/records/paged?stage=${stage}&page=${this.historyPage}&pageSize=12`)
        this.historyTotal = Number(response.total || 0)
        const records = response.items || []
        const merged = (this.historyPage === 1 ? mergePendingGenerations(records, stage) : records)
          .filter(record =>
            record.stage === stage ||
            (record.stage === 'concept' && String(record.id || '').startsWith(`interior-${stage.replace('_', '-')}-`))
          )
        this.history = merged.slice(0, 12).map(record => ({
          id: record.id,
          style: record.style,
          size: record.size,
          status: record.status,
          createdAt: record.createdAt,
          source: record.inputContext && record.inputContext.floorPlanImages && record.inputContext.floorPlanImages[0]
            ? record.inputContext.floorPlanImages[0].url
            : '',
          result: record.images && record.images[0] ? (record.images[0].url || record.images[0]) : '',
          resultWidth: record.images && record.images[0] ? record.images[0].width : null,
          resultHeight: record.images && record.images[0] ? record.images[0].height : null,
          taskIds: (record.tasks || []).map(task => task && task.id).filter(Boolean),
          taskStates: record.tasks || [],
          errorMessage: record.error || record.errorMessage || ''
          , imageModel: record.imageModel || ''
        }))
        const active = merged.find(record => record.status === 'generating')
        if (!active) return
        const taskIds = (active.tasks || []).map(task => task.id).filter(Boolean)
        const source = active.inputContext && active.inputContext.floorPlanImages
          ? active.inputContext.floorPlanImages[0]
          : null
        this.currentBatchId = active.id
        this.currentTaskIds = taskIds
        if (!this.previewUrl && source && source.url) this.previewUrl = source.url
        if (taskIds.length) this.continuePolling(true)
        else this.schedulePendingTaskRefresh()
      } catch (error) {
        this.errorMessage = error.message || '暂时无法恢复家具白底任务'
        this.errorSuggestion = error.suggestion || '请检查网络后刷新页面，已提交的任务记录不会丢失。'
        this.errorRetryable = Boolean(error.pending || error.retryable)
      } finally {
        this.historyLoading = false
      }
    },
    changeHistoryPage(page) {
      this.historyPage = page
      this.loadPendingTask()
    },
    schedulePendingTaskRefresh() {
      if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer)
      if (this.pollingStopped) return
      this.pendingRecordTimer = window.setTimeout(() => {
        this.pendingRecordTimer = null
        this.loadPendingTask()
      }, 2500)
    },
    async continuePolling(silent = false) {
      if (this.generating || !this.currentTaskIds.length) return
      this.generating = true
      this.errorMessage = ''
      this.errorSuggestion = ''
      this.errorRetryable = false
      this.progress = Math.max(18, this.progress)
      this.generationText = this.sceneMode ? '正在恢复家具环境任务进度' : '正在恢复家具白底任务进度'
      try {
        this.resultUrl = await this.pollTask(this.currentTaskIds[0])
        this.progress = 100
        this.generationText = this.sceneMode ? '家具环境图已生成' : '家具白底图已生成'
        if (!silent) this.showMessage('家具白底任务已完成', 'success')
      } catch (error) {
        this.errorMessage = error.message || '查询家具白底任务失败'
        this.errorSuggestion = error.suggestion || '请稍后继续查询。'
        this.errorRetryable = Boolean(error.pending || error.retryable)
        if (!silent) this.showMessage(this.errorMessage, error.pending ? 'warning' : 'error')
      } finally {
        this.generating = false
        this.loadPendingTask()
      }
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.showMessage('提示词已复制', 'success')
      } catch (error) {
        this.showMessage('复制失败，请手动选择提示词', 'warning')
      }
    },
    statusText(status) {
      return {
        generating: '生成中',
        completed: '已完成',
        failed: '生成失败'
      }[status] || '等待处理'
    },
    formatRecordTime(value) {
      if (!value) return '时间未知'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '时间未知'
      return new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date)
    },
    restoreHistoryRecord(record) {
      if (record.source) {
        if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = record.source
      }
      this.resultUrl = record.result || ''
      this.currentBatchId = record.id
      this.currentTaskIds = record.taskIds || []
      this.errorMessage = ''
      this.errorSuggestion = ''
      if (record.status === 'generating' && this.currentTaskIds.length) this.continuePolling(true)
    },
    previewHistoryImage(record, type) {
      const selectedUrl = type === 'source' ? record.source : record.result
      if (!selectedUrl) return
      this.restoreHistoryRecord(record)
      const items = [
        record.source ? { url: record.source, title: '家具原图' } : null,
        record.result ? { url: record.result, title: this.sceneMode ? '家具环境结果图' : '家具白底结果图' } : null
      ].filter(Boolean)
      const index = Math.max(0, items.findIndex(item => item.url === selectedUrl))
      this.$emit('preview-image', selectedUrl, {
        title: items[index].title,
        items,
        index
      })
    },
    async downloadHistoryResult(record) {
      const previous = this.resultUrl
      this.resultUrl = record.result
      try {
        await this.downloadResult()
      } finally {
        this.resultUrl = previous
      }
    },
    async downloadResult() {
      try {
        const blob = await downloadResource(this.resultUrl)
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `furniture-${this.sceneMode ? 'scene' : 'white-background'}-${Date.now()}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
      } catch (error) {
        this.showMessage(error.message || '下载失败，请稍后重试', 'error')
      }
    },
    showMessage(message, type = 'info') {
      this.$message({ message, type, showClose: true, duration: 2600, offset: 24 })
    }
  }
}
</script>

<style scoped>
.white-model-workspace {
  width: calc(100% - 68px);
  max-width: none;
  min-width: 0;
  box-sizing: border-box;
  margin: 26px auto 56px;
  color: #202b25;
}
.workspace-kicker { color: #2563eb; font-size: 9px; font-weight: 700; letter-spacing: 2px; }
.method-head h2 { margin: 7px 0 5px; font-size: 19px; letter-spacing: 0; }

.white-model-main { min-height: 700px; display: grid; grid-template-columns: 340px minmax(0, 1fr); border: 1px solid #dde3de; border-radius: 7px; overflow: hidden; background: #fff; box-shadow: 0 12px 34px rgba(35,48,41,.07); }
.white-model-settings { min-width: 0; padding: 22px; border-right: 1px solid #e3e7e4; background: #fafbfa; }
.setting-section + .setting-section { margin-top: 22px; border-top: 1px solid #e5e9e6; padding-top: 20px; }
.section-title { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.section-title > span { width: 26px; height: 26px; flex: 0 0 26px; display: grid; place-items: center; border-radius: 4px; background: #eaf2ff; color: #2563eb; font-size: 9px; font-weight: 700; }
.section-title > div { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.section-title strong { color: #2d3932; font-size: 12px; }
.section-title small { color: #929a95; font-size: 8px; line-height: 1.45; }
.plan-dropzone { height: 180px; display: grid; place-items: center; position: relative; overflow: hidden; border: 1px dashed #bfc9c2; border-radius: 5px; background: #fff; cursor: pointer; }
.plan-dropzone:hover { border-color: #6692eb; background: #f8faff; }
.plan-dropzone input { display: none; }
.plan-dropzone > img { width: 100%; height: 100%; display: block; object-fit: contain; background: #f1f3f1; }
.dropzone-empty { display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 20px; color: #838d86; text-align: center; }
.dropzone-empty > i { color: #4778d6; font-size: 25px; }
.dropzone-empty strong { color: #46524b; font-size: 11px; }
.dropzone-empty small { font-size: 8px; line-height: 1.5; }
.replace-plan { position: absolute; right: 8px; bottom: 8px; border: 0; border-radius: 3px; padding: 6px 9px; background: rgba(31,43,36,.82); color: #fff; font-size: 9px; }
.file-rule { min-width: 0; display: flex; align-items: center; gap: 6px; margin-top: 8px; color: #929a95; font-size: 8px; }
.file-rule.valid { color: #317e4d; }
.file-rule span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dimension-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }
.dimension-grid label, .note-field { min-width: 0; display: flex; flex-direction: column; gap: 5px; }
.dimension-grid label > span, .note-field > span { display: flex; justify-content: space-between; color: #667169; font-size: 9px; }
.dimension-grid em, .note-field em { color: #9ca49f; font-size: 8px; font-style: normal; font-weight: 400; }
.dimension-grid input { width: 100%; height: 34px; border: 1px solid #d8ded9; border-radius: 4px; outline: 0; padding: 0 9px; background: #fff; color: #344039; font-size: 10px; }
.dimension-grid input:focus, .note-field textarea:focus { border-color: #6692eb; box-shadow: 0 0 0 3px rgba(37,99,235,.08); }
.note-field { margin-top: 12px; }
.note-field textarea { width: 100%; min-height: 64px; resize: vertical; border: 1px solid #d8ded9; border-radius: 4px; outline: 0; padding: 8px 9px; color: #344039; font-size: 9px; line-height: 1.55; }
.output-section { display: flex; flex-direction: column; gap: 9px; }
.output-section .section-title { margin-bottom: 2px; }
.output-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.scene-field { display: grid; grid-template-columns: 76px minmax(0, 1fr); align-items: center; gap: 10px; margin-bottom: 12px; color: #536159; font-size: 10px; }
.scene-field select { width: 100%; min-height: 40px; border: 1px solid #dce2dd; border-radius: 5px; padding: 0 34px 0 11px; background: #fff; color: #344139; font: inherit; outline: none; cursor: pointer; }
.scene-field select:hover { border-color: #9ab2e3; }
.scene-field select:focus-visible { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.output-row > span { color: #68736c; font-size: 9px; }
.output-row .el-select { width: 164px; }
.generate-white-model { width: 100%; min-height: 50px; display: grid; grid-template-columns: 24px 1fr; align-items: center; margin-top: 22px; border: 0; border-radius: 5px; padding: 8px 14px; background: #2563eb; color: #fff; box-shadow: 0 7px 17px rgba(37,99,235,.22); }
.generate-white-model:hover { background: #1d4ed8; }
.generate-white-model:disabled { opacity: .72; cursor: wait; }
.generate-white-model > i, .button-spinner { grid-row: 1 / span 2; font-size: 18px; }
.generate-white-model > span:not(.button-spinner) { text-align: left; font-size: 12px; font-weight: 700; }
.generate-white-model small { opacity: .7; text-align: left; font-size: 8px; }
.button-spinner { width: 17px; height: 17px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: whiteSpin .8s linear infinite; }
.generation-progress { height: 3px; margin-top: 7px; overflow: hidden; border-radius: 2px; background: #dce4df; }
.generation-progress i { height: 100%; display: block; background: #2563eb; transition: width .4s ease; }

.comparison-panel { min-width: 0; display: flex; flex-direction: column; padding: 22px; background: #fff; }
.comparison-head { min-height: 45px; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.comparison-head > div:first-child { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.comparison-head strong { color: #2d3932; font-size: 13px; }
.comparison-head span { color: #8c958f; font-size: 9px; }
.comparison-actions { display: flex; gap: 6px; }
.comparison-actions button { width: 44px; height: 44px; border: 1px solid #dce2dd; border-radius: 4px; background: #fff; color: #536159; }
.comparison-actions button:hover { border-color: #8aacf0; color: #2563eb; }
.comparison-stage { min-height: 540px; display: grid; grid-template-columns: 1fr 1fr; gap: 1px; margin-top: 13px; overflow: hidden; border: 1px solid #dfe4e0; border-radius: 5px; background: #dfe4e0; }
.compare-viewport { min-width: 0; min-height: 0; display: grid; place-items: center; position: relative; overflow: hidden; background: #f3f5f3; }
.compare-viewport > img { width: 100%; height: 100%; display: block; object-fit: contain; background: #f1f3f1; }
.compare-viewport > img.zoomable-stage-image { cursor: zoom-in; transition: filter .18s ease; }
.compare-viewport > img.zoomable-stage-image:hover { filter: brightness(.96); }
.compare-viewport > img.zoomable-stage-image:focus-visible { outline: 3px solid #2563eb; outline-offset: -3px; }
.model-viewport { background: #fff; box-shadow: inset 0 0 0 1px #eef1ef; }
.viewport-label { position: absolute; z-index: 2; left: 10px; top: 10px; border: 1px solid rgba(214,221,216,.9); border-radius: 3px; padding: 5px 7px; background: rgba(255,255,255,.9); color: #516058; font-size: 8px; font-weight: 700; }
.viewport-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 28px; color: #919a94; text-align: center; }
.viewport-empty > i { color: #7890bc; font-size: 29px; }
.viewport-empty strong { color: #536058; font-size: 11px; }
.viewport-empty > span:last-child { max-width: 250px; font-size: 8px; line-height: 1.6; }
.line-art-empty-icon { font-size: 52px; color: #eef3ef; text-shadow: 0 0 18px rgba(255,255,255,.22); }
.wireframe-cube { width: 84px; height: 70px; position: relative; margin-bottom: 7px; transform: rotateX(58deg) rotateZ(45deg); }
.wireframe-cube i { position: absolute; border: 2px solid #7f9fdc; background: rgba(255,255,255,.55); }
.wireframe-cube i:nth-child(1) { inset: 5px 5px 24px 5px; }
.wireframe-cube i:nth-child(2) { width: 28px; height: 32px; left: 5px; bottom: 2px; }
.wireframe-cube i:nth-child(3) { width: 36px; height: 25px; right: 5px; bottom: 2px; }
.wireframe-cube i:nth-child(4) { width: 25px; height: 18px; right: 5px; top: 5px; }
.model-loader { width: 72px; height: 60px; position: relative; margin-bottom: 10px; }
.model-loader i { position: absolute; width: 43px; height: 43px; left: 14px; border: 2px solid #6f94db; transform: rotate(30deg) skew(-8deg); animation: modelPulse 1.5s ease-in-out infinite; }
.model-loader i:nth-child(2) { top: 8px; opacity: .65; animation-delay: .18s; }
.model-loader i:nth-child(3) { top: 16px; opacity: .35; animation-delay: .36s; }
.accuracy-strip { min-height: 48px; display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 7px 16px; border: 1px solid #dfe5e0; border-top: 0; border-radius: 0 0 5px 5px; background: #fafbfa; }
.accuracy-strip span { display: inline-flex; align-items: center; gap: 4px; color: #647068; font-size: 8px; }
.accuracy-strip i { color: #338753; }
.generation-error { display: flex; align-items: flex-start; gap: 9px; margin-top: 12px; border-left: 3px solid #d54a4a; padding: 10px 12px; background: #fff5f5; color: #a13f3f; }
.generation-error > i { margin-top: 1px; }
.generation-error > div { display: flex; flex-direction: column; gap: 3px; }
.generation-error strong { font-size: 10px; }
.generation-error span { font-size: 8px; line-height: 1.55; }

.ai-method-panel { margin-top: 22px; border: 1px solid #dde3de; border-radius: 7px; background: #fff; box-shadow: 0 10px 28px rgba(35,48,41,.05); }
.method-head { min-height: 78px; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 22px; border-bottom: 1px solid #e6eae7; }
.method-head h2 { font-size: 16px; }
.prompt-toggle { display: inline-flex; align-items: center; gap: 6px; border: 1px solid #c8d7f5; border-radius: 4px; padding: 8px 11px; background: #f4f7fd; color: #245cc5; font-size: 9px; }
.method-steps { display: grid; grid-template-columns: repeat(4, 1fr); border-bottom: 1px solid #e6eae7; }
.method-steps article { min-width: 0; min-height: 120px; display: grid; grid-template-columns: 25px 32px 1fr; align-items: flex-start; gap: 8px; padding: 22px 18px; }
.method-steps article + article { border-left: 1px solid #e6eae7; }
.method-steps article > span { width: 24px; height: 24px; display: grid; place-items: center; border-radius: 50%; background: #eaf2ff; color: #2563eb; font-size: 8px; font-weight: 700; }
.method-steps article > i { color: #4f76bd; font-size: 22px; text-align: center; }
.method-steps article > div { min-width: 0; }
.method-steps strong { display: block; color: #344139; font-size: 10px; }
.method-steps p { margin: 5px 0 0; color: #89928c; font-size: 8px; line-height: 1.6; }
.prompt-content { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; padding: 20px 22px 22px; background: #fafbfa; }
.prompt-block { min-width: 0; border: 1px solid #dfe4e0; border-radius: 5px; overflow: hidden; background: #fff; }
.prompt-block header { min-height: 53px; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; border-bottom: 1px solid #e8ebe9; }
.prompt-block header > div { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.prompt-block header span { color: #6f91d4; font-size: 7px; font-weight: 700; letter-spacing: 1.4px; }
.prompt-block header strong { color: #3b4740; font-size: 10px; }
.prompt-block header button { width: 30px; height: 30px; flex: 0 0 30px; border: 1px solid #dfe4e0; border-radius: 4px; background: #fff; color: #647169; }
.prompt-block header button:hover { border-color: #8baaf0; color: #2563eb; }
.prompt-block pre { height: 310px; margin: 0; overflow: auto; padding: 14px; color: #526059; font-family: "Microsoft YaHei", sans-serif; font-size: 9px; line-height: 1.75; white-space: pre-wrap; word-break: break-word; }
.negative-constraints { grid-column: 1 / -1; display: flex; align-items: center; flex-wrap: wrap; gap: 7px; border-left: 3px solid #d34b4b; padding: 10px 12px; background: #fff7f7; }
.negative-constraints strong { margin-right: 4px; color: #944040; font-size: 9px; }
.negative-constraints span { border: 1px solid #ebcece; border-radius: 3px; padding: 4px 7px; background: #fff; color: #8e5d5d; font-size: 8px; }
.prompt-reveal-enter-active, .prompt-reveal-leave-active { transition: opacity .18s ease; }
.prompt-reveal-enter, .prompt-reveal-leave-to { opacity: 0; }

.furniture-history-section { margin-top: 22px; border: 1px solid #dde3de; border-radius: 7px; padding: 22px; background: #fff; box-shadow: 0 10px 28px rgba(35,48,41,.05); }
.history-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; margin-bottom: 17px; }
.history-head h2 { margin: 7px 0 5px; color: #26322b; font-size: 16px; }
.history-head p { margin: 0; color: #87918b; font-size: 9px; line-height: 1.55; }
.history-refresh { min-height: 44px; display: inline-flex; align-items: center; gap: 7px; border: 1px solid #c8d7f5; border-radius: 5px; padding: 0 13px; background: #f4f7fd; color: #245cc5; font-size: 10px; }
.history-refresh:disabled { cursor: wait; opacity: .65; }
.furniture-history-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.furniture-history-grid article { min-width: 0; overflow: hidden; border: 1px solid #dfe4e0; border-radius: 7px; background: #fff; transition: border-color .18s ease, box-shadow .18s ease; }
.furniture-history-grid article:hover, .furniture-history-grid article.active { border-color: #91afea; box-shadow: 0 10px 24px rgba(37,99,235,.1); }
.furniture-history-grid article:focus-visible { outline: 3px solid #2563eb; outline-offset: 2px; }
.history-preview { width: 100%; display: grid; grid-template-columns: 1fr 1fr; position: relative; background: #e5e9e6; }
.history-image { min-width: 0; aspect-ratio: 1 / 1; display: grid; place-items: center; position: relative; overflow: hidden; border: 0; padding: 0; background: #f3f5f3; cursor: zoom-in; }
.history-image:disabled { cursor: default; }
.history-image:focus-visible { z-index: 3; outline: 3px solid #2563eb; outline-offset: -3px; }
.history-image.result { background: #fff; }
.history-image img { width: 100%; height: 100%; display: block; object-fit: contain; transition: transform .2s ease; }
.history-image:not(:disabled):hover img { transform: scale(1.035); }
.history-image > span { display: flex; flex-direction: column; align-items: center; gap: 7px; color: #89928c; font-size: 9px; }
.history-image > span i { font-size: 22px; }
.history-image small { position: absolute; left: 8px; bottom: 8px; border-radius: 3px; padding: 4px 6px; background: rgba(27,39,32,.78); color: #fff; font-size: 8px; }
.history-image.result small { background: rgba(37,99,235,.88); }
.history-zoom { width: 30px; height: 30px; position: absolute; right: 8px; bottom: 8px; display: grid; place-items: center; border-radius: 4px; background: rgba(17,24,39,.76); color: #fff; font-size: 14px; opacity: 0; transform: translateY(3px); transition: opacity .18s ease, transform .18s ease; }
.history-image:hover .history-zoom, .history-image:focus-visible .history-zoom { opacity: 1; transform: translateY(0); }
.history-status { position: absolute; z-index: 2; right: 8px; top: 8px; border-radius: 10px; padding: 4px 7px; background: #eef1ef; color: #69746d; font-size: 8px; }
.history-status.completed { background: #e8f6ed; color: #277447; }
.history-status.generating { background: #edf3ff; color: #245cc5; }
.history-status.failed { background: #fff0f0; color: #b74444; }
.furniture-history-grid footer { min-height: 88px; display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 11px 12px; }
.furniture-history-grid footer > div { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.furniture-history-grid footer strong { overflow: hidden; color: #2d3932; font-size: 11px; white-space: nowrap; text-overflow: ellipsis; }
.history-record-details { display: grid; gap: 3px; margin: 1px 0 0; }
.history-record-details > div { min-width: 0; display: grid; grid-template-columns: 46px minmax(0, 1fr); gap: 7px; align-items: baseline; }
.history-record-details dt { color: #89928c; font-size: 8px; }
.history-record-details dd { min-width: 0; margin: 0; overflow: hidden; color: #5f6b64; font-size: 8px; text-overflow: ellipsis; white-space: nowrap; }
.furniture-history-grid footer button { width: 44px; height: 44px; flex: 0 0 44px; border: 1px solid #dce2dd; border-radius: 5px; background: #fff; color: #536159; }
.furniture-history-grid footer button:hover { border-color: #8aacf0; color: #2563eb; }
.history-empty { min-height: 180px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; border: 1px dashed #d4dad6; border-radius: 6px; background: #fafbfa; color: #8b958f; text-align: center; }
.history-empty > i { color: #7890bc; font-size: 30px; }
.history-empty strong { color: #536058; font-size: 11px; }
.history-empty span { font-size: 8px; }

@keyframes whiteSpin { to { transform: rotate(360deg); } }
@keyframes modelPulse { 0%, 100% { transform: rotate(30deg) skew(-8deg) scale(.96); opacity: .35; } 50% { transform: rotate(30deg) skew(-8deg) scale(1.04); opacity: 1; } }

@media (prefers-reduced-motion: reduce) {
  .button-spinner, .model-loader i { animation: none; }
  .generation-progress i, .prompt-reveal-enter-active, .prompt-reveal-leave-active { transition: none; }
}

@media (max-width: 1120px) {
  .white-model-main { grid-template-columns: 310px minmax(0, 1fr); }
  .comparison-stage { min-height: 460px; }
  .method-steps { grid-template-columns: repeat(2, 1fr); }
  .method-steps article:nth-child(3) { border-top: 1px solid #e6eae7; border-left: 0; }
  .method-steps article:nth-child(4) { border-top: 1px solid #e6eae7; }
  .furniture-history-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 820px) {
  .white-model-workspace { width: calc(100% - 24px); margin-top: 16px; }
  .white-model-main { grid-template-columns: 1fr; }
  .white-model-settings { border-right: 0; border-bottom: 1px solid #e3e7e4; }
  .comparison-stage { min-height: 720px; grid-template-columns: 1fr; grid-template-rows: 1fr 1fr; }
  .prompt-content { grid-template-columns: 1fr; }
  .prompt-block pre { height: 280px; }
}

@media (max-width: 560px) {
  .method-head { align-items: flex-start; flex-direction: column; }
  .method-steps { grid-template-columns: 1fr; }
  .method-steps article + article { border-top: 1px solid #e6eae7; border-left: 0; }
  .comparison-panel { padding: 14px; }
  .comparison-stage { min-height: 620px; }
  .accuracy-strip { align-items: flex-start; flex-direction: column; padding: 12px; }
  .history-head { flex-direction: column; }
  .history-refresh { width: 100%; justify-content: center; }
  .furniture-history-grid { grid-template-columns: 1fr; }
}
</style>
